<script lang="ts" setup>
import Navbar from '@/components/ui/Navbar.vue'
import EnvBadge from '@/components/ui/EnvBadge.vue'
import LoadingSpinner from '@/components/ui/LoadingSpinner.vue'
import { isLoading, isOffline, useMqttConnection } from '@/composables/useMqttConnection'
import { defineAsyncComponent, onMounted, onUnmounted, ref } from 'vue'
import { useUiStore } from '@/stores/ui'
import { useMqttStore } from '@/stores/mqtt'

const DynamicView = defineAsyncComponent(() => import('@/views/DynamicView.vue'))
const DemoDeviceErrorModal = defineAsyncComponent(
  () => import('@/components/DemoDeviceErrorModal.vue'),
)

useMqttConnection() // Initialize MQTT connection at app level

// Initialize variables at the top level, but they'll only be used in development
const isDebugMode = ref(false)
const uiStore = useUiStore()
const mqttStore = useMqttStore()
const isDev = import.meta.env.DEV

// Only attach event listeners in development
if (isDev) {
  const handleKeyPress = (event: KeyboardEvent) => {
    if (event.key === 'd') {
      isDebugMode.value = !isDebugMode.value
      document.body.classList.toggle('debug-mode')
    }
    if (event.key === 'j') {
      uiStore.toggleJsonViewers()
    }
  }

  onMounted(() => {
    window.addEventListener('keydown', handleKeyPress)
  })

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyPress)
  })
}
</script>

<template>
  <Transition name="page">
    <LoadingSpinner v-if="isLoading" />
    <div v-else class="app-container">
      <!-- Debug elements only included in development builds -->
      <template v-if="isDev">
        <div v-if="isDebugMode" class="debug-status">Debug Mode: ON</div>
        <div v-if="uiStore.showJsonViewers" class="json-status">JSON Viewers: ON</div>
      </template>
      <div v-if="isOffline" class="offline-status">Gerät ist offline</div>
      <Navbar />
      <main class="main-content">
        <DynamicView />
      </main>

      <!-- Demo Device Error Modal -->
      <DemoDeviceErrorModal
        :visible="mqttStore.showDemoDeviceError"
        :error-data="mqttStore.demoDeviceError"
        @close="mqttStore.closeDemoDeviceError"
      />
    </div>
  </Transition>

  <!-- Outside the Transition so it is visible during loading too. -->
  <EnvBadge />
</template>

<style scoped>
.app-container {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.main-content {
  flex: 1;
  width: 100%;
  padding: calc(60px + 20px) 20px 20px; /* Adjust for navbar height + additional padding */
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-x: hidden;
}

.offline-status {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 60px; /* Match navbar height */
  background: rgba(255, 59, 48, 0.8);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-weight: bold;
  font-size: 14px;
  z-index: 999;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none; /* Allow clicks to pass through */
}

.json-status {
  position: fixed;
  top: 40px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 5px 10px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 9999;
}

.page-enter-active,
.page-leave-active {
  transition: opacity 0.5s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}
</style>
